import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { formatUnits } from "ethers";
import { ArrowLeft } from "lucide-react";
import { useMemo, useState } from "react";

import {
  baseSepolia,
  polygon,
  bitlayer,
  arbitrum,
  base,
  arbitrumSepolia,
  assetChain,
  assetChainTestnet,
  bitlayerTestnet,
  bsc,
  bscTestnet,
} from "config/chains";
import { AssetChainBridgeInfo } from "lib/apis/bridge/index.dto";
import { Asset } from "lib/bridge/types";
import { getChainName } from "lib/node";
import { ChainId } from "lib/node/types";
import { switchNetwork } from "lib/wallets";
import useWallet from "lib/wallets/useWallet";

import { AssetCard } from "./AssetCard";

interface EnterAmountScreenProps {
  asset: Asset;
  bridgeInfo: AssetChainBridgeInfo | null;
  onBack: () => void;
  onBridge: (amount: string) => void;
  onAmountChange: (amount: string) => void;
  disableButton: boolean;
}

const PERCENTAGES = [10, 25, 50, 80, 100];

function getSwitchChainId(chainId: ChainId) {
  switch (chainId) {
    case "1":
      return baseSepolia.id;
    case "137":
      return polygon.id;
    case "200810":
      return bitlayerTestnet.id;
    case "56":
      return bsc.id;
    case "97":
      return bscTestnet.id;
    case "200901":
      return bitlayer.id;
    case "42420":
      return assetChain.id;
    case "42421":
      return assetChainTestnet.id;
    case "421614":
      return arbitrumSepolia.id;
    case "42161":
      return arbitrum.id;
    case "8453":
      return base.id;
    case "84532":
      return baseSepolia.id;
    default:
      return null;
  }
}

export function EnterAmountScreen({
  asset,
  bridgeInfo,
  onBack,
  onBridge,
  onAmountChange,
  disableButton,
}: EnterAmountScreenProps) {
  const { i18n } = useLingui();
  const numericBalance = parseFloat(asset.balance.replace(/,/g, ""));
  const [amount, setAmount] = useState(asset.balance);
  const [selectedPercent, setSelectedPercent] = useState<number | null>(null);
  const { chainId } = useWallet();

  function handlePercentage(pct: number) {
    setSelectedPercent(pct);
    const val = Math.floor((numericBalance * pct) / 100);
    const valAsString = val.toString();
    setAmount(valAsString);
    onAmountChange(valAsString);
  }

  function handleAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSelectedPercent(null);
    setAmount(e.target.value);
    onAmountChange(e.target.value);
  }

  const displayAmount = amount || asset.balance;

  const buttonText = useMemo(() => {
    if (!chainId) {
      return t`Switch to ${getChainName(asset.chainId as any)}`;
    }
    if (chainId.toString() !== asset.chainId.toString()) {
      return t`Switch to ${getChainName(asset.chainId as any)}`;
    }
    return t`Bridge ${displayAmount} ${asset.symbol}`;
  }, [chainId, i18n.locale, asset.chainId, displayAmount, asset.symbol]);

  function handleButtonClick() {
    if (!chainId) {
      const switchChainId = getSwitchChainId(asset.chainId as ChainId);
      if (switchChainId) {
        switchNetwork(Number(asset.chainId), true);
      }
    } else if (chainId.toString() !== asset.chainId.toString()) {
      const switchChainId = getSwitchChainId(asset.chainId as ChainId);
      if (switchChainId) {
        switchNetwork(Number(asset.chainId), true);
      }
    } else {
      onBridge(amount);
    }
  }

  return (
    <div className="mt-[60px] flex flex-col  px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto flex w-full max-w-6xl">
        <button
          onClick={onBack}
          className="py-2.5 hover:bg-muted flex w-fit items-center gap-2 rounded-full bg-[#1E1E1F] px-10 py-6 text-[16px] text-[#ffff6] ring-1 ring-[#fff56] transition-colors hover:ring-[#FFF] md:text-[20px]"
          aria-label={t`Go back`}
        >
          <ArrowLeft />
          <Trans>Back</Trans>
        </button>
      </div>

      <div className="mx-auto my-6 flex w-full max-w-3xl flex-1 flex-col  md:my-10 md:gap-20">
        <h1 className="text-center text-24 font-bold md:text-40">
          <Trans>Enter amount</Trans>
        </h1>
        <p className="mt-3 text-pretty text-center text-[14px] text-[#ffffff66] md:text-[16px]">
          <Trans>How much would you like to transfer to Nectarliquid from the selected asset?</Trans>
        </p>

        <div className="mt-8 w-full md:mt-10">
          <AssetCard
            name={asset.name}
            balance={asset.balance}
            iconVariant={asset.iconVariant}
            tokenImage={asset.tokenImage}
            chainImage={asset.chainImage}
            selected
          />
        </div>

        <div className="mt-8 w-full">
          <label className=" mb-10 block text-[14px] text-[#ffffff66] md:text-[16px]">
            <Trans>Enter amount</Trans>
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder={t`Enter amount`}
              value={amount}
              onChange={handleAmountChange}
              className=" border-border placeholder:text-muted-foreground w-full rounded-full border px-10 py-20 text-[14px] text-white transition-all  focus:outline-none focus:ring-1  focus:ring-[#00EC97] md:px-14 md:py-18 md:text-[16px]"
            />
            <span className="absolute right-15 top-1/2 -translate-y-1/2 text-[14px] font-medium text-white md:text-16">
              {asset.name}
            </span>
          </div>
        </div>

        {bridgeInfo && (
          <div className="mt-8 w-full  text-center">
            <p className=" mb-10 block text-[14px] text-white md:text-[16px]">
              <Trans>
                Bridge limit: {formatUnits(bridgeInfo.limitPerSend, asset.decimals)} {asset.symbol}
              </Trans>
            </p>
          </div>
        )}

        <div className="mt-4 flex w-full flex-wrap justify-center gap-2 md:gap-8">
          {PERCENTAGES.map((pct) => (
            <button
              key={pct}
              onClick={() => handlePercentage(pct)}
              className={`md:py-2.5 rounded-full border bg-[#00EC970D] px-4 py-4 text-[14px] font-medium transition-all md:px-10 md:text-[18px] ${
                selectedPercent === pct
                  ? "border-[#00EC97] text-[#00EC97] "
                  : "border-border text-[#00EC97] hover:border-[#00EC97]/50 hover:bg-[#00EC97]/5"
              }`}
            >
              {pct}%
            </button>
          ))}
        </div>

        <button
          onClick={handleButtonClick}
          className={`mx-auto mt-20 h-[64px] w-[304px] rounded-full bg-[#00EC97] text-14 font-semibold text-black transition-all hover:brightness-110 active:scale-[0.98] md:text-24 ${disableButton ? "cursor-not-allowed opacity-50" : ""}`}
          disabled={disableButton}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}
